/**
 * التقارير.
 * المعاينة flex:2 1 420px + قائمة التقارير flex:1 1 260px ⇒ عمود واحد أقل من 760px.
 * التصدير تجريبي في PHASE 1 (لا توليد PDF فعلي).
 */

import { useState } from 'react';
import { color } from '@/design/tokens';
import { Button, Card, KpiCell, SectionTitle, SegmentedControl } from '@/components/primitives/Primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { UnavailablePanel } from '@/components/states/Unavailable';
import { useReportPreview, useReports } from '@/services/market';
import { useToast } from '@/app/AppShell';
import type { ReportType } from '@/domain/types';

const TYPES: { value: ReportType; label: string }[] = [
  { value: 'district', label: 'تقرير حي' },
  { value: 'valuation', label: 'تقرير تقييم' },
  { value: 'portfolio', label: 'تقرير محفظة' },
];

export default function ReportsRoute(): JSX.Element {
  const toast = useToast();
  const [type, setType] = useState<ReportType>('district');
  const preview = useReportPreview(type);
  const list = useReports();

  return (
    <div className="nq-enter" style={{ paddingTop: 36 }}>
      <SectionTitle
        title="التقارير"
        action={<SegmentedControl options={TYPES} value={type} onChange={setType} ariaLabel="نوع التقرير" />}
      />

      <div className="nq-row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: '2 1 420px', minWidth: 0 }}>
          {preview.status === 'unavailable' ? (
            <UnavailablePanel
              reason="coming-soon"
              title="التقارير"
              detail="توليد التقارير يعتمد على مؤشرات لم تُطلق بعد."
            />
          ) : preview.status === 'loading' ? (
            <LoadingState message="نجهّز معاينة التقرير..." />
          ) : preview.status === 'error' || !preview.data ? (
            <ErrorState message="تعذّر توليد معاينة التقرير." onRetry={preview.reload} />
          ) : (
            <Card as="article">
              <div
                style={{
                  paddingBottom: 16,
                  borderBottom: `1px solid ${color.hairlineSoft}`,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    padding: '3px 9px',
                    border: '1px solid rgba(240,162,75,.32)',
                    borderRadius: 5,
                    color: color.amber,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  بيانات تجريبية
                </span>
                <h2 style={{ marginTop: 12, fontSize: 22, fontWeight: 600 }}>{preview.data.title}</h2>
                <p style={{ marginTop: 6, fontSize: 13, color: color.textSubtle }}>{preview.data.subtitle}</p>
              </div>

              <div className="nq-grid-numbers" style={{ marginTop: 18 }}>
                {preview.data.kpis.map((k) => (
                  <KpiCell key={k.label} label={k.label} value={k.value} unit={k.unit} />
                ))}
              </div>

              <div style={{ display: 'grid', gap: 20, marginTop: 24 }}>
                {preview.data.sections.map((s) => (
                  <section key={s.title}>
                    <h3 style={{ fontSize: 15, fontWeight: 600 }}>{s.title}</h3>
                    <p style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.75, color: color.textBody }}>
                      {s.body}
                    </p>
                  </section>
                ))}
              </div>

              <section style={{ marginTop: 24 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600 }}>حدود التقرير</h3>
                <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
                  {preview.data.limitations.map((l) => (
                    <li key={l} style={{ fontSize: 12.5, color: color.textSubtle, lineHeight: 1.7 }}>
                      · {l}
                    </li>
                  ))}
                </ul>
              </section>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 24 }}>
                <Button onClick={() => toast('التصدير تجريبي في هذه المرحلة')}>تصدير (تجريبي)</Button>
                <Button variant="ghost" onClick={() => toast('نُسخ رابط التقرير')}>
                  مشاركة
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <Card variant="secondary">
            <div className="nq-label">تقارير سابقة</div>
            {list.status === 'loading' ? (
              <div style={{ marginTop: 12 }}>
                <LoadingState blocks={2} />
              </div>
            ) : list.status === 'empty' ? (
              <div style={{ marginTop: 12 }}>
                <EmptyState title="لا توجد تقارير" description="أنشئ تقريراً من صفحة الحي أو من نتيجة تقييم." />
              </div>
            ) : (
              <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                {(list.data ?? []).map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setType(r.type)}
                      style={{
                        width: '100%',
                        textAlign: 'start',
                        padding: '11px 10px',
                        background: 'transparent',
                        border: 0,
                        borderRadius: 9,
                        cursor: 'pointer',
                        color: color.textBody,
                        fontSize: 13,
                        minHeight: 44,
                      }}
                    >
                      <div>{r.title}</div>
                      <div className="nq-num" dir="ltr" style={{ fontSize: 10.5, color: color.micro, marginTop: 3 }}>
                        {r.createdAt}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
